import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  User,
  CalendarCheck2,
  Wallet,
  LogOut,
  Menu,
  X,
  FileText,
  CalendarOff,
  CheckSquare,
  Sparkles,
  ClipboardList,
  Ticket,
  Gift,
  Banknote,
  Award,
  Megaphone,
  Trophy,
  ClipboardCheck,
  Briefcase,
  ListChecks,
} from "lucide-react";
import PrincipalSwitcher from "@/components/PrincipalSwitcher";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/my-portal/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/my-portal/announcements", label: "Announcements", icon: Megaphone, hasUnreadBadge: true },
  { to: "/my-portal/profile", label: "My Profile", icon: User },
  { to: "/my-portal/attendance", label: "Attendance", icon: CalendarCheck2 },
  { to: "/my-portal/leaves", label: "Leaves", icon: CalendarOff },
  { to: "/my-portal/holidays", label: "Holidays", icon: Sparkles },
  { to: "/delegation/tasks", label: "My Tasks", icon: ClipboardList },
  { to: "/my-portal/todos", label: "My To-Dos", icon: ListChecks },
  { to: "/checklist", label: "My Checklist", icon: ClipboardList },
  { to: "/my-portal/requests", label: "My Requests", icon: Ticket },
  { to: "/my-portal/advances", label: "Salary Advance", icon: Banknote },
  { to: "/my-portal/festivals", label: "Festival Gifts", icon: Gift },
  { to: "/my-portal/salary", label: "Salary Slips", icon: Wallet },
  { to: "/my-portal/appraisal", label: "Appraisal", icon: Award },
  { to: "/my-portal/performance", label: "My Performance", icon: Trophy },
  { to: "/my-portal/documents", label: "My Documents", icon: FileText },
  { to: "/my-portal/assets", label: "My Assets", icon: Briefcase },
];

// HODs / Senior Mgmt / Manager / EA can also delegate tasks + handle service requests + approvals
const MANAGER_NAV = [
  { to: "/my-portal/approvals", label: "Team Approvals", icon: CheckSquare },
  { to: "/delegation/create", label: "Delegate Tasks", icon: ClipboardList },
  { to: "/admin/requests", label: "Service Requests", icon: Ticket },
  { to: "/admin/approvals/requests", label: "Approval Queue", icon: CheckSquare },
];

// Only HOD + Senior Mgmt can access the team-level Performance Scoring report
const HOD_SR_NAV = [
  { to: "/admin/performance", label: "Team Performance", icon: Trophy },
];

// Senior Mgmt's own weekly checklist (read + edit own templates + self-log)
const SM_OWN_NAV = [
  { to: "/my-portal/sm-checklist", label: "SM Weekly Checklist", icon: ClipboardCheck },
  { to: "/admin/sm-checklist/monthly", label: "SM Monthly Summary", icon: ClipboardCheck },
];

const ASSIGNER_ROLES = ["senior_management", "hod", "manager", "ea"];

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/broadcasts/my-feed/unread-count");
        if (!cancelled) setUnreadAnnouncements(data.count || 0);
      } catch { /* silent */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = () => (
    <>
      <div className="px-5 py-5 border-b border-red-100">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 rounded-md bg-white border border-red-100 flex items-center justify-center p-1 flex-shrink-0">
            <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="text-slate-900 font-bold text-sm leading-tight" style={{ fontFamily: "Chivo" }}>
              My Portal
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Bravura Self-Service
            </div>
          </div>
        </div>
      </div>
      <nav className="flex-1 py-3 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `mx-2 my-0.5 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "nav-item-active"
                    : "text-slate-700 hover:bg-red-50"
                }`
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.hasUnreadBadge && unreadAnnouncements > 0 && (
                <span className="bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="announce-badge">
                  {unreadAnnouncements}
                </span>
              )}
            </NavLink>
          );
        })}
        {(user?.role === "hod" || user?.is_senior_management || ASSIGNER_ROLES.includes(user?.role)) && (
          <>
            <div className="px-5 mt-4 mb-1 text-[10px] uppercase tracking-widest text-slate-400">
              Manager
            </div>
            {MANAGER_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `mx-2 my-0.5 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "nav-item-active"
                        : "text-slate-700 hover:bg-red-50"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
            {(user?.role === "hod" || user?.role === "senior_management") && HOD_SR_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `mx-2 my-0.5 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "nav-item-active"
                        : "text-slate-700 hover:bg-red-50"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
            {user?.role === "senior_management" && SM_OWN_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `mx-2 my-0.5 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "nav-item-active"
                        : "text-slate-700 hover:bg-red-50"
                    }`
                  }
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </>
        )}
      </nav>
      <div className="border-t border-red-100 p-4 space-y-2">
        <div>
          <div className="text-xs text-slate-500 mb-1">Signed in as</div>
          <div className="text-sm font-medium text-slate-900 truncate" data-testid="portal-user-name">
            {user?.name}
          </div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
        <PrincipalSwitcher tone="light" />
        <button
          data-testid="portal-logout-btn"
          onClick={onLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" />
          Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside
        data-testid="portal-sidebar"
        className="hidden lg:flex w-60 bg-white border-r border-red-100 flex-col flex-shrink-0"
      >
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setOpen(false)}
          />
          <aside
            data-testid="portal-sidebar-mobile"
            className="fixed top-0 left-0 z-50 h-full w-60 bg-white border-r border-red-100 flex flex-col lg:hidden"
          >
            <Sidebar />
          </aside>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 lg:h-16 bg-white border-b border-red-100 flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-700 hover:bg-slate-100 rounded"
              onClick={() => setOpen(true)}
              data-testid="portal-mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">
                Welcome / Swagat
              </div>
              <div className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: "Chivo" }}>
                {user?.name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div
              className="h-9 w-9 rounded-full brand-bg text-white flex items-center justify-center text-sm font-bold overflow-hidden"
              data-testid="portal-header-avatar"
            >
              {user?.profile_photo_url ? (
                <img src={`/api/files/${user.profile_photo_url}`} alt="" className="h-full w-full object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() || "U"
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
